/**
 * TierIndicator Component
 *
 * A visual indicator showing the user's current pricing tier with:
 * - Tier comparison card showing current tier with previous/next tier pricing
 * - Progress bar showing position within the current tier
 * - Animated transitions when crossing tier boundaries
 *
 * @see src/modules/RetoolModule/types/pricingCalculator.ts - Tier calculations
 * @see src/modules/RetoolModule/components/PricingCalculator/PricingCalculatorFormulas.tsx - Related tier visual
 */

import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  alpha,
  useTheme,
  Tooltip,
  Chip,
  GlobalStyles,
  Theme,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Zap as ZapIcon,
} from 'lucide-react';
import {
  PlanType,
  calculatePlatformFee,
  calculateSeatPricePerMonth,
} from '../../types/pricingCalculator';

// Define tier data for different pricing components
export interface TierData {
  minUsers: number;
  maxUsers: number;
  label: string;
  value: string;
  rate?: number;
}

const formatCurrency2 = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Shown instead of a price where the active plan is not sold at that tier's size. */
const TIER_UNAVAILABLE = 'n/a';

/** A zero fee means the plan is not sold at that size, not that it is free. */
const formatTierFee = (fee: number) => (fee > 0 ? `$${formatCurrency2(fee)}/yr` : TIER_UNAVAILABLE);

/**
 * Platform fee tier boundaries (from calculatePlatformFee). Boundaries are
 * stable across plan types — only the fee differs, and since GLX-1931 each
 * plan has its own published column rather than a multiplier off Standard.
 * Keeping these as bounds + a generator means the visualizer can never drift
 * from the actual platform fee field again (GLX-1675).
 */
const PLATFORM_FEE_TIER_BOUNDS: ReadonlyArray<{ min: number; max: number }> = [
  { min: 1, max: 25 },
  { min: 26, max: 51 },
  { min: 52, max: 101 },
  { min: 102, max: 151 },
  { min: 152, max: 201 },
  { min: 202, max: 251 },
  { min: 252, max: 301 },
  { min: 302, max: 351 },
  { min: 352, max: 401 },
  { min: 402, max: 451 },
  { min: 452, max: 501 },
  { min: 502, max: 601 },
  { min: 602, max: 701 },
  { min: 702, max: 801 },
  { min: 802, max: 901 },
  { min: 902, max: 1001 },
  { min: 1002, max: 1251 },
  { min: 1252, max: 1501 },
  { min: 1502, max: 1751 },
  { min: 1752, max: 2001 },
  { min: 2002, max: 2251 },
  { min: 2252, max: 2501 },
  { min: 2502, max: 2751 },
  { min: 2752, max: 3001 },
  { min: 3002, max: 3251 },
  { min: 3252, max: 3501 },
  { min: 3502, max: Infinity },
];

/**
 * Build the platform-fee tier list for a given plan type, sampled via the
 * canonical `calculatePlatformFee` so the card always matches what the Platform
 * Fee field charges. Pre-fix this was a static table that capped at Tier 13
 * (602-701 users) and was plan-agnostic — for any team above 701 the card froze
 * on "Tier 13 / $30,030/yr" while the actual fee kept rising.
 *
 * Where a band is sampled matters, because two plan rules cut across the band
 * boundaries rather than following them (GLX-1931): the Base plan is a flat
 * $999 at or below 15 members, and Enterprise is not sold below 200. Two bands
 * are therefore straddled and hold two different rates —
 *   - 1-25 on Base: $999 up to 15 members, then $1,500
 *   - 152-201 on Enterprise: not sold below 200, then $19,305
 * so neither bound alone is the band's rate. Sampling only the lower bound
 * showed $0.00/yr for a 200-member Enterprise deal billing $19,305; sampling
 * only the upper bound showed $1,500/yr for a 10-member Base deal billing $999.
 *
 * So: when `userCount` falls inside a band, that band is sampled at the exact
 * member count, which is what makes the card agree with the Platform Fee field.
 * Every other band is sampled at both bounds and quotes a span when they differ,
 * rather than silently picking a side.
 */
export const getPlatformFeeTiers = (planType: PlanType, userCount?: number): TierData[] =>
  PLATFORM_FEE_TIER_BOUNDS.map((bound, i) => {
    const base = {
      minUsers: bound.min,
      maxUsers: bound.max,
      label: `Tier ${i + 1}`,
    };

    // The band the customer is actually in: quote their exact fee.
    if (userCount !== undefined && userCount >= bound.min && userCount <= bound.max) {
      const fee = calculatePlatformFee(userCount, planType).platformFee;
      return { ...base, value: formatTierFee(fee), rate: fee };
    }

    // The open-ended final band has no finite upper bound to sample.
    const lowFee = calculatePlatformFee(bound.min, planType).platformFee;
    const highFee = Number.isFinite(bound.max)
      ? calculatePlatformFee(bound.max, planType).platformFee
      : lowFee;

    // A zero on one side means the plan is not sold across part of the band;
    // quote the side where it is, rather than a "$0.00–$19,305" range.
    const soldLow = lowFee > 0 ? lowFee : highFee;
    const soldHigh = highFee > 0 ? highFee : lowFee;

    return {
      ...base,
      value:
        soldLow === soldHigh
          ? formatTierFee(soldHigh)
          : `${formatTierFee(soldLow).replace('/yr', '')}–${formatTierFee(soldHigh)}`,
      rate: soldHigh,
    };
  });

/**
 * Member seat-price tier boundaries (from calculateSeatPricePerMonth).
 * Bounds are stable; the rate is plan-dependent (Enterprise is 1.5×).
 */
const SEAT_PRICE_TIER_BOUNDS: ReadonlyArray<{ min: number; max: number }> = [
  { min: 1, max: 249 },
  { min: 250, max: 499 },
  { min: 500, max: 999 },
  { min: 1000, max: 4999 },
  { min: 5000, max: Infinity },
];

/**
 * Build the member seat-price tier list for a given plan type. Sampled at
 * each tier's min user count via `calculateSeatPricePerMonth` so the card
 * reflects the Enterprise 1.5× multiplier when applicable.
 */
export const getSeatPriceTiers = (planType: PlanType): TierData[] =>
  SEAT_PRICE_TIER_BOUNDS.map((bound, i) => {
    const rate = calculateSeatPricePerMonth(bound.min, planType);
    return {
      minUsers: bound.min,
      maxUsers: bound.max,
      label: `Tier ${i + 1}`,
      value: `$${formatCurrency2(rate)}/mo`,
      rate,
    };
  });

// Backward-compatible static defaults (Standard plan). Prefer the
// `getPlatformFeeTiers` / `getSeatPriceTiers` helpers in new code so the
// rates reflect the active plan.
export const PLATFORM_FEE_TIERS: TierData[] = getPlatformFeeTiers(PlanType.STANDARD);
export const SEAT_PRICE_TIERS: TierData[] = getSeatPriceTiers(PlanType.STANDARD);

// SSO tiers based on calculateSsoFeePerUser function
export const SSO_TIERS: TierData[] = [
  { minUsers: 1, maxUsers: 49, label: 'Tier 1', value: '$0.50/user/mo', rate: 0.5 },
  { minUsers: 50, maxUsers: 99, label: 'Tier 2', value: '$0.40/user/mo', rate: 0.4 },
  { minUsers: 100, maxUsers: 249, label: 'Tier 3', value: '$0.38/user/mo', rate: 0.38 },
  { minUsers: 250, maxUsers: 499, label: 'Tier 4', value: '$0.30/user/mo', rate: 0.3 },
  { minUsers: 500, maxUsers: 999, label: 'Tier 5', value: '$0.25/user/mo', rate: 0.25 },
  { minUsers: 1000, maxUsers: 1999, label: 'Tier 6', value: '$0.23/user/mo', rate: 0.23 },
  { minUsers: 2000, maxUsers: 2999, label: 'Tier 7', value: '$0.21/user/mo', rate: 0.21 },
  { minUsers: 3000, maxUsers: 3999, label: 'Tier 8', value: '$0.18/user/mo', rate: 0.18 },
  { minUsers: 4000, maxUsers: 4999, label: 'Tier 9', value: '$0.17/user/mo', rate: 0.17 },
  { minUsers: 5000, maxUsers: Infinity, label: 'Tier 10+', value: '$0.15/user/mo', rate: 0.15 },
];

// Animation names (keyframes injected via GlobalStyles to avoid styled-components v4 issues)
const pulseAnimation = 'tier-indicator-pulse';
const glowAnimation = 'tier-indicator-glow';
const slideInAnimation = 'tier-indicator-slideIn';

// Global keyframe styles - injected once to avoid styled-components v4 keyframe interpolation issues
const TierIndicatorKeyframes = () => (
  <GlobalStyles
    styles={`
      @keyframes ${pulseAnimation} {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }
      @keyframes ${glowAnimation} {
        0% { box-shadow: 0 0 5px rgba(59, 130, 246, 0.3); }
        50% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.6); }
        100% { box-shadow: 0 0 5px rgba(59, 130, 246, 0.3); }
      }
      @keyframes ${slideInAnimation} {
        0% { opacity: 0; transform: translateX(-10px); }
        100% { opacity: 1; transform: translateX(0); }
      }
    `}
  />
);

export type TierType = 'platform' | 'sso' | 'seat';

export interface TierIndicatorProps {
  /** Current number of users/members */
  userCount: number;
  /** Type of tier to display */
  tierType: TierType;
  /**
   * Active plan type. Required for plan-dependent tiers (`platform`, `seat`)
   * so the displayed rate matches what the calculator actually charges.
   * Defaults to STANDARD; passing the wrong plan here is the bug GLX-1675
   * fixed (the indicator was previously plan-agnostic and capped at Tier 13).
   */
  planType?: PlanType;
  /** Optional custom title */
  title?: string;
  /** Show compact version */
  compact?: boolean;
  /** Show tier comparison with previous/next */
  showComparison?: boolean;
  /** Show progress within tier */
  showProgress?: boolean;
  /** Enable animations when tier changes */
  enableAnimations?: boolean;
  /** Allow navigating through tiers by clicking dots or cards */
  allowNavigation?: boolean;
}

// Helper to get tiers based on type. Plan-dependent tiers (platform, seat)
// are generated dynamically so their rates always reflect the active plan.
const getTiersByType = (
  tierType: TierType,
  planType: PlanType = PlanType.STANDARD,
  userCount?: number,
): TierData[] => {
  switch (tierType) {
    case 'platform':
      return getPlatformFeeTiers(planType, userCount);
    case 'sso':
      return SSO_TIERS;
    case 'seat':
      return getSeatPriceTiers(planType);
    default:
      return getPlatformFeeTiers(planType, userCount);
  }
};

// Helper to find current tier index
const findTierIndex = (userCount: number, tiers: TierData[]): number => {
  const index = tiers.findIndex((tier) => userCount >= tier.minUsers && userCount <= tier.maxUsers);
  return index >= 0 ? index : tiers.length - 1;
};

// Helper to calculate progress within tier
const calculateTierProgress = (userCount: number, tier: TierData): number => {
  if (tier.maxUsers === Infinity) return 100;
  const range = tier.maxUsers - tier.minUsers + 1;
  const position = userCount - tier.minUsers;
  return Math.min(100, Math.max(0, (position / range) * 100));
};

// Helper to calculate users until next tier
const getUsersUntilNextTier = (userCount: number, tier: TierData): number => {
  if (tier.maxUsers === Infinity) return 0;
  return tier.maxUsers - userCount + 1;
};

/** Left card: the tier below the one in view (dimmed when there is none). */
const PreviousTierCard: React.FC<{
  theme: Theme;
  previousTier: TierData | null;
  allowNavigation: boolean;
  onPreviousTierClick: () => void;
}> = ({ theme, previousTier, allowNavigation, onPreviousTierClick }) => (
  <Box
    onClick={previousTier ? onPreviousTierClick : undefined}
    sx={{
      flex: 1,
      p: 1.5,
      borderRadius: '8px',
      backgroundColor: alpha(theme.palette.grey[500], 0.05),
      opacity: previousTier ? 1 : 0.3,
      cursor: previousTier && allowNavigation ? 'pointer' : 'default',
      transition: 'all 0.2s ease',
      '&:hover':
        previousTier && allowNavigation
          ? {
              backgroundColor: alpha(theme.palette.grey[500], 0.1),
              transform: 'scale(1.02)',
            }
          : {},
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
      <ChevronLeftIcon size={14} color={theme.palette.text.secondary} />
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
        }}
      >
        Previous
      </Typography>
    </Box>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {previousTier?.label || '—'}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        color: 'text.secondary',
        fontFamily: 'monospace',
      }}
    >
      {previousTier?.value || 'N/A'}
    </Typography>
    {/* User count threshold for previous tier */}
    {previousTier && (
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mt: 0.5,
          fontSize: '0.65rem',
          color: theme.palette.text.disabled,
        }}
      >
        {previousTier.minUsers.toLocaleString()}-
        {previousTier.maxUsers === Infinity ? '∞' : previousTier.maxUsers.toLocaleString()} users
      </Typography>
    )}
  </Box>
);

/** Middle card: the tier currently in view, highlighted. */
const CurrentTierCard: React.FC<{
  theme: Theme;
  currentTier: TierData;
  isViewingDifferentTier: boolean;
  tierChanged: boolean;
  tierDirection: 'up' | 'down' | null;
}> = ({ theme, currentTier, isViewingDifferentTier, tierChanged, tierDirection }) => (
  <Box
    sx={{
      flex: 1,
      p: 1.5,
      borderRadius: '8px',
      backgroundColor: isViewingDifferentTier
        ? alpha(theme.palette.info.main, 0.1)
        : alpha(theme.palette.primary.main, 0.1),
      border: '2px solid',
      borderColor: isViewingDifferentTier ? theme.palette.info.main : theme.palette.primary.main,
      position: 'relative',
    }}
  >
    {tierChanged && tierDirection && !isViewingDifferentTier && (
      <Box
        sx={{
          position: 'absolute',
          top: -8,
          right: -8,
          backgroundColor: theme.palette.primary.main,
          borderRadius: '50%',
          width: 20,
          height: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: `${pulseAnimation} 0.5s ease-in-out`,
        }}
      >
        {tierDirection === 'up' ? (
          <TrendingUpIcon size={12} color="#fff" />
        ) : (
          <TrendingDownIcon size={12} color="#fff" />
        )}
      </Box>
    )}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
      <ZapIcon
        size={14}
        color={isViewingDifferentTier ? theme.palette.info.main : theme.palette.primary.main}
      />
      <Typography
        variant="caption"
        sx={{
          color: isViewingDifferentTier ? theme.palette.info.main : theme.palette.primary.main,
          fontWeight: 600,
        }}
      >
        {isViewingDifferentTier ? 'Viewing' : 'Current'}
      </Typography>
    </Box>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 700,
        color: isViewingDifferentTier ? theme.palette.info.main : theme.palette.primary.main,
      }}
    >
      {currentTier.label}
    </Typography>
    <Typography
      variant="caption"
      sx={{ fontFamily: 'monospace', color: theme.palette.success.main, fontWeight: 600 }}
    >
      {currentTier.value}
    </Typography>
    {/* User count threshold for current tier */}
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        mt: 0.5,
        fontSize: '0.65rem',
        color: isViewingDifferentTier ? theme.palette.info.main : theme.palette.primary.main,
        opacity: 0.8,
      }}
    >
      {currentTier.minUsers.toLocaleString()}-
      {currentTier.maxUsers === Infinity ? '∞' : currentTier.maxUsers.toLocaleString()} users
    </Typography>
  </Box>
);

/** Right card: the tier above the one in view (dimmed at the best rate). */
const NextTierCard: React.FC<{
  theme: Theme;
  nextTier: TierData | null;
  allowNavigation: boolean;
  onNextTierClick: () => void;
}> = ({ theme, nextTier, allowNavigation, onNextTierClick }) => (
  <Box
    onClick={nextTier ? onNextTierClick : undefined}
    sx={{
      flex: 1,
      p: 1.5,
      borderRadius: '8px',
      backgroundColor: alpha(theme.palette.grey[500], 0.05),
      opacity: nextTier ? 1 : 0.3,
      cursor: nextTier && allowNavigation ? 'pointer' : 'default',
      transition: 'all 0.2s ease',
      '&:hover':
        nextTier && allowNavigation
          ? {
              backgroundColor: alpha(theme.palette.grey[500], 0.1),
              transform: 'scale(1.02)',
            }
          : {},
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
        }}
      >
        Next
      </Typography>
      <ChevronRightIcon size={14} color={theme.palette.text.secondary} />
    </Box>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {nextTier?.label || 'Max'}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        color: 'text.secondary',
        fontFamily: 'monospace',
      }}
    >
      {nextTier?.value || 'Best rate'}
    </Typography>
    {/* User count threshold for next tier */}
    {nextTier && (
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mt: 0.5,
          fontSize: '0.65rem',
          color: theme.palette.text.disabled,
        }}
      >
        {nextTier.minUsers.toLocaleString()}-
        {nextTier.maxUsers === Infinity ? '∞' : nextTier.maxUsers.toLocaleString()} users
      </Typography>
    )}
  </Box>
);

type TierComparisonProps = {
  theme: Theme;
  previousTier: TierData | null;
  currentTier: TierData;
  nextTier: TierData | null;
  isViewingDifferentTier: boolean;
  allowNavigation: boolean;
  tierChanged: boolean;
  tierDirection: 'up' | 'down' | null;
  onPreviousTierClick: () => void;
  onNextTierClick: () => void;
};

/**
 * Previous / current / next tier cards. Extracted from TierIndicator to keep both
 * functions under the repo complexity ceilings; rendering is unchanged.
 */
const TierComparison: React.FC<TierComparisonProps> = ({
  theme,
  previousTier,
  currentTier,
  nextTier,
  isViewingDifferentTier,
  allowNavigation,
  tierChanged,
  tierDirection,
  onPreviousTierClick,
  onNextTierClick,
}) => (
  <Box sx={{ display: 'flex', p: 1.5, gap: 1.5 }}>
    {/* Previous Tier */}
    <PreviousTierCard
      theme={theme}
      previousTier={previousTier}
      allowNavigation={allowNavigation}
      onPreviousTierClick={onPreviousTierClick}
    />

    {/* Current Tier (highlighted) */}
    <CurrentTierCard
      theme={theme}
      currentTier={currentTier}
      isViewingDifferentTier={isViewingDifferentTier}
      tierChanged={tierChanged}
      tierDirection={tierDirection}
    />

    {/* Next Tier */}
    <NextTierCard
      theme={theme}
      nextTier={nextTier}
      allowNavigation={allowNavigation}
      onNextTierClick={onNextTierClick}
    />
  </Box>
);

export const TierIndicator: React.FC<TierIndicatorProps> = ({
  userCount,
  tierType,
  planType = PlanType.STANDARD,
  title,
  compact = false,
  showComparison = true,
  showProgress = true,
  enableAnimations = true,
  allowNavigation = true,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const tiers = useMemo(
    () => getTiersByType(tierType, planType, userCount),
    [tierType, planType, userCount],
  );
  const actualTierIndex = useMemo(() => findTierIndex(userCount, tiers), [userCount, tiers]);

  // Allow viewing different tiers when navigation is enabled
  const [viewedTierIndex, setViewedTierIndex] = useState<number | null>(null);

  // Reset viewed tier when actual tier changes
  // Use ref to track previous value and setTimeout(0) to defer state update
  // This avoids React Compiler warning about cascading renders
  const prevActualTierIndexRef = useRef(actualTierIndex);
  useEffect(() => {
    if (prevActualTierIndexRef.current !== actualTierIndex) {
      prevActualTierIndexRef.current = actualTierIndex;
      // Defer state update to avoid cascading render warning
      setTimeout(() => {
        setViewedTierIndex(null);
      }, 0);
    }
  }, [actualTierIndex]);

  const currentTierIndex = viewedTierIndex ?? actualTierIndex;
  const isViewingDifferentTier = viewedTierIndex !== null && viewedTierIndex !== actualTierIndex;

  const currentTier = tiers[currentTierIndex];
  const previousTier = currentTierIndex > 0 ? tiers[currentTierIndex - 1] : null;
  const nextTier = currentTierIndex < tiers.length - 1 ? tiers[currentTierIndex + 1] : null;

  // Navigation handlers
  const handleDotClick = useCallback(
    (index: number) => {
      if (allowNavigation) {
        setViewedTierIndex(index === actualTierIndex ? null : index);
      }
    },
    [allowNavigation, actualTierIndex],
  );

  const handlePreviousTierClick = useCallback(() => {
    if (allowNavigation && currentTierIndex > 0) {
      setViewedTierIndex(currentTierIndex - 1 === actualTierIndex ? null : currentTierIndex - 1);
    }
  }, [allowNavigation, currentTierIndex, actualTierIndex]);

  const handleNextTierClick = useCallback(() => {
    if (allowNavigation && currentTierIndex < tiers.length - 1) {
      setViewedTierIndex(currentTierIndex + 1 === actualTierIndex ? null : currentTierIndex + 1);
    }
  }, [allowNavigation, currentTierIndex, tiers.length, actualTierIndex]);

  const progress = useMemo(
    () => calculateTierProgress(userCount, currentTier),
    [userCount, currentTier],
  );
  const usersUntilNext = useMemo(
    () => getUsersUntilNextTier(userCount, currentTier),
    [userCount, currentTier],
  );

  // Track tier changes for animations using refs and deferred state updates
  // This pattern avoids cascading render warnings by using setTimeout(0) for state updates
  const prevTierIndexRef = useRef(currentTierIndex);
  const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [tierChanged, setTierChanged] = useState(false);
  const [tierDirection, setTierDirection] = useState<'up' | 'down' | null>(null);

  // Handle animation on tier change using deferred state updates
  useEffect(() => {
    const prevIndex = prevTierIndexRef.current;
    if (prevIndex !== currentTierIndex) {
      const direction: 'up' | 'down' = currentTierIndex > prevIndex ? 'up' : 'down';
      prevTierIndexRef.current = currentTierIndex;

      // Clear any existing animation timer
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
      }

      // Use setTimeout(0) to defer state updates and avoid cascading render warning
      // This schedules the update for the next event loop tick
      setTimeout(() => {
        setTierChanged(true);
        setTierDirection(direction);
      }, 0);

      // Reset animation state after animation completes
      animationTimerRef.current = setTimeout(() => {
        setTierChanged(false);
        setTierDirection(null);
      }, 600);
    }

    return () => {
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
      }
    };
  }, [currentTierIndex]);

  const defaultTitle = useMemo(() => {
    switch (tierType) {
      case 'platform':
        return 'Platform Fee Tier';
      case 'sso':
        return 'SSO Rate Tier';
      case 'seat':
        return 'Member Price Tier';
      default:
        return 'Pricing Tier';
    }
  }, [tierType]);

  // Compact version - just shows the chip
  if (compact) {
    return (
      <>
        <TierIndicatorKeyframes />
        <Tooltip
          title={
            <Box sx={{ p: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {currentTier.label}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {currentTier.value}
              </Typography>
              {usersUntilNext > 0 && nextTier && (
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary, display: 'block', mt: 0.5 }}
                >
                  Next tier at {(currentTier.maxUsers + 1).toLocaleString()} users
                </Typography>
              )}
            </Box>
          }
          arrow
        >
          <Chip
            icon={<ZapIcon size={14} />}
            label={currentTier.label}
            size="small"
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              fontWeight: 600,
              animation:
                enableAnimations && tierChanged ? `${pulseAnimation} 0.3s ease-in-out` : undefined,
            }}
          />
        </Tooltip>
      </>
    );
  }

  return (
    <>
      <TierIndicatorKeyframes />
      <Box
        sx={{
          borderRadius: '12px',
          border: '1px solid',
          borderColor: alpha(theme.palette.primary.main, 0.2),
          backgroundColor: isDark
            ? alpha(theme.palette.primary.main, 0.05)
            : alpha(theme.palette.primary.main, 0.02),
          overflow: 'hidden',
          animation:
            enableAnimations && tierChanged ? `${glowAnimation} 0.6s ease-in-out` : undefined,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid',
            borderColor: theme.palette.divider,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '8px',
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.primary.main,
              }}
            >
              <ZapIcon size={18} />
            </Box>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                }}
              >
                {title || defaultTitle}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  animation:
                    enableAnimations && tierChanged
                      ? `${slideInAnimation} 0.3s ease-out`
                      : undefined,
                }}
              >
                {currentTier.label}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ textAlign: 'right' }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
              }}
            >
              Current Rate
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                fontFamily: 'monospace',
                color: theme.palette.success.main,
              }}
            >
              {currentTier.value}
            </Typography>
          </Box>
        </Box>

        {/* Progress Bar */}
        {showProgress && (
          <Box
            sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: theme.palette.divider }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {currentTier.minUsers.toLocaleString()} users
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: theme.palette.primary.main }}
              >
                {userCount.toLocaleString()} ({Math.round(progress)}%)
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {currentTier.maxUsers === Infinity ? '∞' : currentTier.maxUsers.toLocaleString()}{' '}
                users
              </Typography>
            </Box>
            <Box
              sx={{
                height: 8,
                borderRadius: '4px',
                backgroundColor: alpha(theme.palette.grey[500], 0.15),
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${progress}%`,
                  borderRadius: '4px',
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  transition: 'width 0.3s ease-out',
                }}
              />
            </Box>
            {usersUntilNext > 0 && nextTier ? (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.5,
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                Next tier at {(currentTier.maxUsers + 1).toLocaleString()} users ({nextTier.value})
              </Typography>
            ) : (
              /* Empty placeholder to maintain consistent card height */
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.5,
                  visibility: 'hidden',
                }}
              >
                &nbsp;
              </Typography>
            )}
          </Box>
        )}

        {/* Tier Comparison */}
        {showComparison && (previousTier || nextTier) && (
          <TierComparison
            theme={theme}
            previousTier={previousTier}
            currentTier={currentTier}
            nextTier={nextTier}
            isViewingDifferentTier={isViewingDifferentTier}
            allowNavigation={allowNavigation}
            tierChanged={tierChanged}
            tierDirection={tierDirection}
            onPreviousTierClick={handlePreviousTierClick}
            onNextTierClick={handleNextTierClick}
          />
        )}

        {/* Tier Progress Dots */}
        <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
            {tiers.slice(0, Math.min(tiers.length, 10)).map((tier, index) => {
              const isActualTier = index === actualTierIndex;
              const isViewedTier = index === currentTierIndex;
              const userRange =
                tier.maxUsers === Infinity
                  ? `${tier.minUsers.toLocaleString()}+ users`
                  : `${tier.minUsers.toLocaleString()}-${tier.maxUsers.toLocaleString()} users`;

              return (
                <Tooltip
                  key={index}
                  title={
                    <Box sx={{ textAlign: 'center', p: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {tier.label}: {tier.value}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
                        {userRange}
                      </Typography>
                      {isActualTier && !isViewedTier && (
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', color: 'primary.main', mt: 0.5 }}
                        >
                          (Your current tier)
                        </Typography>
                      )}
                      {allowNavigation && !isViewedTier && (
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', opacity: 0.6, mt: 0.5 }}
                        >
                          Click to view
                        </Typography>
                      )}
                    </Box>
                  }
                  arrow
                >
                  <Box
                    onClick={() => handleDotClick(index)}
                    sx={{
                      width: isViewedTier ? 16 : 8,
                      height: 8,
                      borderRadius: '4px',
                      backgroundColor: isViewedTier
                        ? isViewingDifferentTier
                          ? theme.palette.info.main
                          : theme.palette.primary.main
                        : isActualTier
                          ? alpha(theme.palette.primary.main, 0.6)
                          : index < actualTierIndex
                            ? alpha(theme.palette.primary.main, 0.4)
                            : alpha(theme.palette.grey[500], 0.2),
                      cursor: allowNavigation ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                      border:
                        isActualTier && isViewingDifferentTier
                          ? `2px solid ${theme.palette.primary.main}`
                          : 'none',
                      '&:hover': allowNavigation
                        ? {
                            transform: 'scaleY(1.5)',
                            backgroundColor: isViewedTier
                              ? isViewingDifferentTier
                                ? theme.palette.info.main
                                : theme.palette.primary.main
                              : alpha(theme.palette.primary.main, 0.5),
                          }
                        : {},
                    }}
                  />
                </Tooltip>
              );
            })}
            {tiers.length > 10 && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  ml: 0.5,
                }}
              >
                +{tiers.length - 10}
              </Typography>
            )}
          </Box>
          {/* Return to current tier link when viewing different tier */}
          {isViewingDifferentTier && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Typography
                variant="caption"
                onClick={() => setViewedTierIndex(null)}
                sx={{
                  color: theme.palette.primary.main,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  '&:hover': { opacity: 0.8 },
                }}
              >
                Return to your tier ({tiers[actualTierIndex].label})
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default TierIndicator;
